import { Trans } from '@lingui/macro'

import * as React from 'react'

import {
  BUTTON_LARGE_FONT_SIZE,
  BUTTON_LARGE_VERTICAL_PADDING,
  ButtonLarge,
} from '~ui/components/buttons/ButtonSizes/ButtonLarge'
import { ButtonWithBack } from '~ui/components/buttons/ButtonWithBack/ButtonWithBack'
import { Text } from '~ui/components/typography/Text/Text'
import { getTextFontSizeInPx } from '~ui/styleGuide/fontStyles'
import { getSpacingValueInPx } from '~ui/styleGuide/spaces'

import { useCartContentCtx, useCartFetchingCtx } from '../contexts/CartContentProvider'
import { validateCartContent } from '../helpers/validateCartContent'
import { useMinOrderPriceToast } from '../hooks/useMinOrderPriceToast'

const DEFAULT_TEXT_LINE_HEIGHT = 1.15

const buttonTextHeight = getTextFontSizeInPx(BUTTON_LARGE_FONT_SIZE) * DEFAULT_TEXT_LINE_HEIGHT
const buttonSpaceHeight = 2 * getSpacingValueInPx(BUTTON_LARGE_VERTICAL_PADDING)
const buttonHeight = buttonTextHeight + buttonSpaceHeight

export const CART_SUBMIT_BUTTON_HEIGHT = buttonHeight

type TColor = PropsOf<typeof ButtonWithBack>['bgColor']

type TProps = NoChildren & {
  /**
   * can we proceed with the order?
   */
  canSubmitOrder: boolean
  /**
   * click handler of the back button, if it is defined, ButtonWithBack is rendered
   */
  onBackClick?: PropsOf<typeof ButtonWithBack>['onBackClick']
  /**
   * if the order cannot be submitted (`canSubmitOrder: false`),
   * the button is blocked and this function is executed on click
   */
  onBlockedClick?: PropsOf<typeof ButtonWithBack>['onBlockedClick']
  /**
   * function to be executed when the submit button is clicked
   */
  onSubmit: () => void
}

/**
 * Button to validate the cart (FE validation) and proceed to the next step of the order.
 * - If the order cannot be submitted (`canSubmitOrder: false`),
 * the button is `blocked` and the `onBlockedClick` function is executed on click.
 * - If the order can be submitted, the `onSubmit` function is executed on click.
 * - The button is `disabled` if the order is loading or the cart is empty.
 *
 * If `onBackClick` is defined the ButtonWithBack is rendered instead of the regular button.
 */
export const CartSubmitButton: React.FC<TProps> = ({
  canSubmitOrder,
  onBackClick,
  onSubmit,
  onBlockedClick,
}) => {
  const isLoading = useCartFetchingCtx()

  const bgColor: TColor = canSubmitOrder ? `conversion` : `secondary`
  const order = useCartContentCtx()

  const presentMinOrderPriceToast = useMinOrderPriceToast(order)

  const { isCartEmpty, isUnderMinimalPrice } = validateCartContent(order)

  const label = canSubmitOrder ? <Trans>continue</Trans> : <Trans>We are closed</Trans>
  const isDisabled = canSubmitOrder && (isLoading || isCartEmpty)

  const buttonState = React.useMemo<PropsOf<typeof ButtonLarge>['state']>(() => {
    if (!canSubmitOrder || isUnderMinimalPrice) return `blocked`
    if (isLoading) return `loading`
    if (isDisabled) return `disabled`

    return `enabled`
  }, [canSubmitOrder, isDisabled, isLoading, isUnderMinimalPrice])

  const informCustomer = canSubmitOrder ? presentMinOrderPriceToast : onBlockedClick

  if (onBackClick) {
    return (
      <ButtonWithBack
        buttonSize={`large`}
        onBackClick={onBackClick}
        onBlockedClick={informCustomer}
        onClick={onSubmit}
        mainButtonState={buttonState}
        bgColor={bgColor}
      >
        <Text fontStyle={`upperCase`}>{label}</Text>
      </ButtonWithBack>
    )
  }
  return (
    <ButtonLarge
      state={buttonState}
      bgColor={bgColor}
      onClick={onSubmit}
      onBlockedClick={informCustomer}
    >
      {label}
    </ButtonLarge>
  )
}
